import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { useTheme } from '../contexts/ThemeContext';
import { api } from '../config/api';
import { BuildingOffice2Icon } from '@heroicons/react/24/outline';

interface InstitutionSummary {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  logo_url: string | null;
  cover_image_url: string | null;
  is_active: boolean;
  group_count: number | null;
  member_count: number | null;
}

const InstitutionSelector: React.FC = () => {
  const { isDarkMode } = useTheme();
  const navigate = useNavigate();
  const [institutions, setInstitutions] = useState<InstitutionSummary[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchInstitutions();
  }, []);

  const fetchInstitutions = async () => {
    try {
      const response = await api.get('/api/institutions');
      const data = response.data;
      setInstitutions(data);

      // Auto-redirect if only one institution
      if (data.length === 1) {
        navigate(`/institution/${data[0].id}`, { replace: true });
      }
    } catch (error) {
      console.error('Failed to fetch institutions:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-500"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-4xl mx-auto p-6">
        <h1 className={`text-2xl font-bold mb-6 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
          Your Institutions
        </h1>

        {institutions.length === 0 ? (
          <div className={`text-center py-12 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            <BuildingOffice2Icon className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p>You are not part of any institution yet.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {institutions.map((inst) => (
              <button
                key={inst.id}
                onClick={() => navigate(`/institution/${inst.id}`)}
                className={`text-left p-6 rounded-xl border transition-all hover:shadow-lg ${
                  isDarkMode
                    ? 'bg-slate-800/50 border-slate-700/50 hover:border-cyan-500/50'
                    : 'bg-white border-gray-200 hover:border-cyan-500/50'
                }`}
              >
                <div className="flex items-center gap-4">
                  {inst.logo_url ? (
                    <img src={inst.logo_url} alt={inst.name} className="h-12 w-12 rounded-lg object-cover" />
                  ) : (
                    <div className={`h-12 w-12 rounded-lg flex items-center justify-center ${
                      isDarkMode ? 'bg-slate-700' : 'bg-gray-100'
                    }`}>
                      <BuildingOffice2Icon className="h-6 w-6 text-cyan-500" />
                    </div>
                  )}
                  <div>
                    <h3 className={`font-semibold text-lg ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                      {inst.name}
                    </h3>
                    {inst.description && (
                      <p className={`text-sm mt-1 line-clamp-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        {inst.description}
                      </p>
                    )}
                  </div>
                </div>
                <div className={`flex gap-4 mt-4 text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  <span>{inst.group_count ?? 0} groups</span>
                  <span>{inst.member_count ?? 0} members</span>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default InstitutionSelector;
